import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp,
  RotateCcw,
  Info,
  DollarSign,
  Loader2,
  Check,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/toast";
import { setCompleted } from "../../lib/uwProgress";
import { getTotalRooms } from "../../lib/rooms";
import { formatCurrency } from "../../lib/utils";
import { Deal, RoomRevenueModel, MonthRow } from "../../types/deal";
import {
  seasonalityPresets,
  SeasonalityPresetKey,
} from "../../data/seasonalityPresets";
import { monthNames } from "../../lib/dateMath";
import { computeMonthRow, rollupTotals } from "../../lib/roomRevenue";
import PresetCard from "./room-revenue/PresetCard";
import SetAdrDialog from "./room-revenue/SetAdrDialog";
import { patchProjectKpis, getDataSource } from "../../lib/datasource";
import type { Kpis } from "../../lib/kpis/defaults";
import {
  normalizeRoomRevenue,
  hasValidRoomRevenue,
} from "../../lib/deals/kpisNormalizers";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
} from "recharts";
import { debounce } from "@/utils/useDebounce";
import { isSupabase } from "@/config/constansts";

type TemplateModeProps = {
  /** When provided, run in template mode and ignore dealId-based persistence */
  kpis?: Kpis | null;
  onChange?: (next: Kpis) => void;
  hideActions?: boolean;
};

type ProjectModeProps = {
  dealId: string;
  onSaved?: () => void;
  refreshKey?: number | string;
};

type Props = ProjectModeProps & TemplateModeProps;

interface RoomRevenueProps extends Props {}

// Helper function to convert room revenue format
function convertRoomRevFormat(roomRev: any): any[] {
  if (!roomRev) return [];

  if (
    roomRev.adr &&
    roomRev.occ &&
    Array.isArray(roomRev.adr) &&
    Array.isArray(roomRev.occ)
  ) {
    return roomRev.adr.map((adr: number, i: number) => ({
      adr: adr || 0,
      occ: roomRev.occ[i] || 0,
    }));
  }

  return [];
}

// Updated createDefaultModel function that checks for template KPIs first
function createDefaultModel(deal: Deal, templateKpis?: any): RoomRevenueModel {
  const rooms = getTotalRooms(deal);
  const year = new Date().getFullYear();

  // Check for template KPIs first - PRIORITY 1
  if (templateKpis) {
    // Check multiple possible formats
    let roomRevData: any[] | null = null;

    // Try roomRevenue.adr/occ format first (new)
    if (
      templateKpis.roomRevenue?.adr &&
      templateKpis.roomRevenue?.occ &&
      Array.isArray(templateKpis.roomRevenue.adr) &&
      Array.isArray(templateKpis.roomRevenue.occ)
    ) {
      roomRevData = templateKpis.roomRevenue.adr.map(
        (adr: number, i: number) => ({
          adr: adr || 0,
          occ: templateKpis.roomRevenue.occ[i] || 0,
        }),
      );
      console.info(
        "[RoomRevenue] Using template KPIs from roomRevenue.adr/occ format",
      );
    }
    // Try roomRevenue.monthly format
    else if (
      templateKpis.roomRevenue?.monthly &&
      Array.isArray(templateKpis.roomRevenue.monthly)
    ) {
      roomRevData = templateKpis.roomRevenue.monthly.map((m: any) => ({
        adr: m.adr || 0,
        occ: m.occ || 0,
      }));
      console.info(
        "[RoomRevenue] Using template KPIs from roomRevenue.monthly format",
      );
    }
    // Try legacy roomRev format
    else if (templateKpis.roomRev?.adr && templateKpis.roomRev?.occ) {
      roomRevData = convertRoomRevFormat(templateKpis.roomRev);
      console.info("[RoomRevenue] Using template KPIs from roomRev format");
    }
    // Try direct monthly array
    else if (
      Array.isArray(templateKpis.roomRevenue) &&
      templateKpis.roomRevenue.length > 0
    ) {
      roomRevData = templateKpis.roomRevenue.map((m: any) => ({
        adr: m.adr || 0,
        occ: m.occ || 0,
      }));
      console.info(
        "[RoomRevenue] Using template KPIs from direct array format",
      );
    }

    if (roomRevData && roomRevData.length > 0) {
      const months = roomRevData.map((data: any, i: number) =>
        computeMonthRow(
          i,
          year,
          rooms,
          Number(data.adr || 0),
          Number(data.occ || 0),
        ),
      );

      const totals = rollupTotals(months);

      return {
        seasonalityPreset: detectPreset(months),
        months,
        totals,
        currency: deal.currency,
      };
    }
  }

  if (
    deal.kpis?.roomRev &&
    Array.isArray(deal.kpis.roomRev.adr) &&
    deal.kpis.roomRev.adr.length === 12 &&
    Array.isArray(deal.kpis.roomRev.occ) &&
    deal.kpis.roomRev.occ.length === 12
  ) {
    console.info(
      "[RoomRevenue] Using KPIs from legacy deal.kpis.roomRev (Migration step 1)",
    );
    const adrArr = deal.kpis.roomRev.adr;
    const occArr = deal.kpis.roomRev.occ;

    const months: MonthRow[] = monthNames.map((_, i) =>
      computeMonthRow(
        i,
        year,
        rooms,
        Number(adrArr[i] || 0),
        Number(occArr[i] || 0),
      ),
    );

    return {
      seasonalityPreset: detectPreset(months),
      months,
      totals: rollupTotals(months),
      currency: deal.currency,
    };
  }

  if (
    deal.kpis?.roomRevenue &&
    Array.isArray(deal.kpis.roomRevenue.adr) &&
    deal.kpis.roomRevenue.adr.length === 12 &&
    Array.isArray(deal.kpis.roomRevenue.occ) &&
    deal.kpis.roomRevenue.occ.length === 12
  ) {
    console.info(
      "[RoomRevenue] Using KPIs from new deal.kpis.roomRevenue.adr/occ",
    );
    const adrArr = deal.kpis.roomRevenue.adr;
    const occArr = deal.kpis.roomRevenue.occ;

    const months: MonthRow[] = monthNames.map((_, i) =>
      computeMonthRow(
        i,
        year,
        rooms,
        Number(adrArr[i] || 0),
        Number(occArr[i] || 0),
      ),
    );

    return {
      seasonalityPreset: deal.kpis.roomRevenue.preset || detectPreset(months),
      months,
      totals: rollupTotals(months),
      currency: deal.currency,
    };
  }

  const normalizedRoomRev = normalizeRoomRevenue(
    deal.kpis?.roomRev || deal.kpis?.roomRevenue,
  );

  if (normalizedRoomRev && hasValidRoomRevenue(normalizedRoomRev)) {
    console.info("[RoomRevenue] Using KPIs from normalizer fallback");
    const adrArr = normalizedRoomRev.monthly.adr;
    const occArr = normalizedRoomRev.monthly.occ;

    const months: MonthRow[] = monthNames.map((_, i) =>
      computeMonthRow(
        i,
        year,
        rooms,
        Number(adrArr[i] || 0),
        Number(occArr[i] || 0),
      ),
    );

    return {
      seasonalityPreset: detectPreset(months),
      months,
      totals: rollupTotals(months),
      currency: deal.currency,
    };
  }

  // Only use defaults if no template data and no deal KPIs exist
  console.info(
    "[RoomRevenue] No template or deal KPIs found, using defaults (ADR=140)",
  );
  const defaultADR = 140;
  const defaultOccupancy = seasonalityPresets.majorCity;

  const months = Array.from({ length: 12 }, (_, i) =>
    computeMonthRow(i, year, rooms, defaultADR, defaultOccupancy[i]),
  );

  const totals = rollupTotals(months);

  return {
    seasonalityPreset: "majorCity",
    months,
    totals,
    currency: deal.currency,
  };
}

function detectPreset(months: MonthRow[]): SeasonalityPresetKey | "custom" {
  const occupancies = months.map((m) => m.occPct);
  for (const [preset, values] of Object.entries(seasonalityPresets)) {
    const matches = values.every(
      (val, i) => Math.abs(val - occupancies[i]) <= 1,
    );
    if (matches) return preset as SeasonalityPresetKey;
  }
  return "custom";
}

export default function RoomRevenue(props: RoomRevenueProps) {
  const { dealId, onSaved, refreshKey, kpis, onChange, hideActions } = props;

  const isTemplate = !!kpis && typeof onChange === "function";

  // Template mode: use prop-driven state
  if (isTemplate) {
    const local = kpis!;

    // Helper to update KPI fields immutably
    const updateField = (path: string, value: any) => {
      const setAtPath = (obj: any, p: string, v: any) => {
        const parts = p
          .replace(/\[(\d+)\]/g, ".$1")
          .split(".")
          .filter(Boolean);
        const clone: any = Array.isArray(obj) ? [...obj] : { ...obj };
        let cur = clone;
        parts.forEach((key, i) => {
          if (i === parts.length - 1) cur[key] = v;
          else {
            const nxt = cur[key];
            cur[key] = Array.isArray(nxt)
              ? [...nxt]
              : typeof nxt === "object" && nxt != null
                ? { ...nxt }
                : {};
            cur = cur[key];
          }
        });
        return clone;
      };
      onChange!(setAtPath(local, path, value));
    };

    const adrArray = Array.isArray(local.roomRevenue?.adr)
      ? local.roomRevenue.adr
      : Array(12).fill(0);
    const occArray = Array.isArray(local.roomRevenue?.occ)
      ? local.roomRevenue.occ
      : Array(12).fill(0);
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Room Revenue
        </h3>
        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-medium text-slate-900">Monthly ADR</h4>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {adrArray.map((val: number, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-8 text-xs text-slate-500">
                    {monthNames[i]}
                  </span>
                  <input
                    type="number"
                    className="border rounded px-2 py-1 w-full"
                    value={val ?? 0}
                    onChange={(e) => {
                      const next = [...adrArray];
                      next[i] = Number(e.target.value) || 0;
                      updateField("roomRevenue.adr", next);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-slate-900">
              Monthly Occupancy (%)
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {occArray.map((val: number, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-8 text-xs text-slate-500">
                    {monthNames[i]}
                  </span>
                  <input
                    type="number"
                    className="border rounded px-2 py-1 w-full"
                    value={val ?? 0}
                    onChange={(e) => {
                      const next = [...occArray];
                      next[i] = Number(e.target.value) || 0;
                      updateField("roomRevenue.occ", next);
                    }}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { toast } = useToast();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [model, setModel] = useState<RoomRevenueModel | null>(null);
  const [originalModel, setOriginalModel] = useState<RoomRevenueModel | null>(
    null,
  );
  const [saveState, setSaveState] = useState<"idle" | "saving" | "success">(
    "idle",
  );
  const [animatedFields, setAnimatedFields] = useState<Set<string>>(new Set());
  const [showSetAdrDialog, setShowSetAdrDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadRoomRevenueData() {
      if (!dealId) return;

      setLoading(true);

      try {
        // 1. Get project data from DB
        const ds = getDataSource();
        const project = await ds.getProject(dealId);

        if (!project) {
          console.error("[RoomRevenue] Project not found:", dealId);
          toast.error("Project not found");
          setLoading(false);
          return;
        }

        setDeal(project as Deal);

        const roomRevenueModel = createDefaultModel(
          project as Deal,
          project.kpis,
        );

        console.info("[RoomRevenue] Model created/loaded from DB data.");

        // 4. Set model states
        setModel(roomRevenueModel);
        setOriginalModel(roomRevenueModel);
      } catch (error) {
        console.error("[RoomRevenue] Error loading room revenue data:", error);
        toast.error("Failed to load room revenue data");
      } finally {
        setLoading(false);
      }
    }

    loadRoomRevenueData();
  }, [dealId, refreshKey]);

  const animateFields = useCallback((fields: string[]) => {
    setAnimatedFields(new Set(fields));
    setTimeout(() => setAnimatedFields(new Set()), 1000);
  }, []);

  const debouncedRecalculate = useCallback(
    debounce((newModel: RoomRevenueModel) => {
      const updatedTotals = rollupTotals(newModel.months);
      const finalModel = { ...newModel, totals: updatedTotals };
      setModel(finalModel);
      animateFields(["totals", "kpis"]);
    }, 200),
    [animateFields],
  );

  const handlePresetSelect = (preset: SeasonalityPresetKey) => {
    if (!model || !deal) return;

    const rooms = getTotalRooms(deal);
    const year = new Date().getFullYear();
    const occupancies = seasonalityPresets[preset];

    const newMonths = model.months.map((month, i) =>
      computeMonthRow(i, year, rooms, month.adr, occupancies[i]),
    );

    const newModel = {
      ...model,
      seasonalityPreset: preset,
      months: newMonths,
    };

    debouncedRecalculate(newModel);
    animateFields(["preset", "table"]);
  };

  const handleCellChange = (
    monthIndex: number,
    field: "adr" | "occPct",
    value: number,
  ) => {
    if (!model || !deal) return;

    const rooms = getTotalRooms(deal);
    const year = new Date().getFullYear();

    const newMonths = [...model.months];
    const currentMonth = newMonths[monthIndex];

    if (field === "adr") {
      newMonths[monthIndex] = computeMonthRow(
        monthIndex,
        year,
        rooms,
        value,
        currentMonth.occPct,
      );
    } else {
      // Clamp occupancy between 0–100
      const clampedValue = Math.max(0, Math.min(100, value));
      newMonths[monthIndex] = computeMonthRow(
        monthIndex,
        year,
        rooms,
        currentMonth.adr,
        clampedValue,
      );
    }

    const detectedPreset = detectPreset(newMonths);
    const newModel = {
      ...model,
      seasonalityPreset: detectedPreset,
      months: newMonths,
    };

    debouncedRecalculate(newModel);
    animateFields([`month-${monthIndex}`, "totals"]);
  };

  const handleResetBaseline = () => {
    if (!deal) return;
    const newModel = createDefaultModel(deal);
    setModel(newModel);
    animateFields(["preset", "table", "totals", "kpis"]);
    toast.success("Reset to baseline assumptions");
  };

  const handleSetAdr = (adr: number, applyTo: "all" | "low" | "high") => {
    if (!model || !deal) return;

    const rooms = getTotalRooms(deal);
    const year = new Date().getFullYear();

    const newMonths = model.months.map((month, i) => {
      let shouldApply = false;
      switch (applyTo) {
        case "all":
          shouldApply = true;
          break;
        case "low":
          shouldApply = month.occPct <= 60;
          break;
        case "high":
          shouldApply = month.occPct >= 80;
          break;
      }
      if (shouldApply) {
        return computeMonthRow(i, year, rooms, adr, month.occPct);
      }
      return month;
    });

    const newModel = { ...model, months: newMonths };
    debouncedRecalculate(newModel);
    animateFields(["table", "totals", "kpis"]);
    toast.success(
      `ADR updated for ${applyTo === "all" ? "all" : applyTo} months`,
    );
  };

  const handleSave = async () => {
    if (!deal || !model) return;

    setSaveState("saving");

    try {
      if (isSupabase) {
        const months = Array.isArray(model.months) ? model.months : [];
        const to12 = (arr: any[]) => {
          const n = (arr ?? []).map((x) => Number(x ?? 0)).slice(0, 12);
          while (n.length < 12) n.push(0);
          return n;
        };
        const adr = to12(months.map((m) => m.adr));
        const occ = to12(months.map((m) => m.occPct));

        const nextKpis = { ...deal.kpis };
        delete nextKpis.roomRev;

        await patchProjectKpis(dealId, {
          ...nextKpis,
          roomRevenue: {
            adr: adr,
            occ: occ,
            monthly: adr.map((a, i) => ({ adr: a, occ: occ[i] })),
            totals: model.totals,
            preset: model.seasonalityPreset ?? null,
          },
          avgADR: model.totals?.avgADR ?? null,
          avgOccupancy: model.totals?.avgOccPct ?? null,
          avgRevPAR: model.totals?.avgRevPAR ?? null,
          totalRoomsRevenue: model.totals?.roomsRevenue ?? null,
        } as any);

        console.info(
          "[RoomRevenue] Saved to Supabase (new format only). Legacy `roomRev` removed.",
        );
      }

      setCompleted(dealId, "roomRevenue", true);
      setOriginalModel(model);
      setSaveState("success");
      toast.success("Room revenue saved");
      onSaved?.();
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (error: any) {
      setSaveState("idle");
      console.error("[RoomRevenue] save error:", error);
      toast.error(error?.message || "Failed to save room revenue");
    }
  };

  const handleCancel = () => {
    if (originalModel) {
      setModel(originalModel);
      toast.info("Changes discarded");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading room revenue data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!deal || !model) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          <p className="text-slate-600">Loading data...</p>
        </div>
      </div>
    );
  }

  const rooms = getTotalRooms(deal);
  if (rooms === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Room Revenue
          </h3>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center space-x-2">
              <Info className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800 font-medium">
                Add rooms in Property Details to model revenue.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getAnimationClass = (field: string) =>
    animatedFields.has(field)
      ? "animate-pulse bg-brand-50 transition-all duration-500"
      : "";

  // Chart data
  const chartData = model.months.map((month, i) => ({
    month: monthNames[i],
    adr: Math.round(month.adr),
    occupancy: Math.round(month.occPct * 10) / 10,
    revpar: Math.round(month.revpar),
    roomsSold: Math.round(month.roomsSold),
    roomsRevenue: Math.round(month.roomsRevenue),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Room Revenue
          </h3>
          <p className="text-sm text-slate-600">
            Model monthly ADR and occupancy patterns with seasonality presets
            and live calculations.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSetAdrDialog(true)}
            className="flex items-center space-x-2"
          >
            <DollarSign className="h-4 w-4" />
            <span>Set Base ADR</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetBaseline}
            className="flex items-center space-x-2"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reset to Baseline</span>
          </Button>
        </div>
      </div>

      {/* Visual Seasonality Presets */}
      <div className={`space-y-4 ${getAnimationClass("preset")}`}>
        <h4 className="font-medium text-slate-900">Seasonality Presets</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PresetCard
            id="beach"
            title="Beach Resort"
            subtitle="Low winter, high summer"
            months={seasonalityPresets.beach}
            active={model.seasonalityPreset === "beach"}
            onSelect={() => handlePresetSelect("beach")}
          />
          <PresetCard
            id="winterResort"
            title="Winter Resort"
            subtitle="High winter, low summer"
            months={seasonalityPresets.winterResort}
            active={model.seasonalityPreset === "winterResort"}
            onSelect={() => handlePresetSelect("winterResort")}
          />
          <PresetCard
            id="majorCity"
            title="City Hotel"
            subtitle="Strong year-round"
            months={seasonalityPresets.majorCity}
            active={model.seasonalityPreset === "majorCity"}
            onSelect={() => handlePresetSelect("majorCity")}
          />
          <PresetCard
            id="businessCity"
            title="Business Hotel"
            subtitle="Weekday heavy, softer summer"
            months={seasonalityPresets.businessCity}
            active={model.seasonalityPreset === "businessCity"}
            onSelect={() => handlePresetSelect("businessCity")}
          />
        </div>
      </div>

      {/* KPI Cards */}
      <div
        className={`grid grid-cols-2 lg:grid-cols-4 gap-4 ${getAnimationClass("kpis")}`}
      >
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">
            Total Rooms Revenue
          </div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(model.totals?.roomsRevenue, deal.currency)}
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Avg ADR</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(model.totals?.avgADR, deal.currency)}
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Avg Occupancy</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {model.totals?.avgOccPct?.toFixed(1)}%
          </div>
        </div>
        <div className="rounded-xl border border-white/50 bg-card-gradient backdrop-blur-sm p-4 shadow-card">
          <div className="text-sm font-medium text-navy-600">Avg RevPAR</div>
          <div className="mt-2 text-2xl font-semibold text-navy-900">
            {formatCurrency(model.totals?.avgRevPAR, deal.currency)}
          </div>
        </div>
      </div>

      {/* Monthly Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 px-4 font-medium text-slate-700">
                Month
              </th>
              <th className="text-right py-3 px-4 font-medium text-slate-700">
                ADR ({deal.currency})
              </th>
              <th className="text-right py-3 px-4 font-medium text-slate-700">
                Occupancy (%)
              </th>
              <th className="text-right py-3 px-4 font-medium text-slate-700">
                RevPAR ({deal.currency})
              </th>
              <th className="text-right py-3 px-4 font-medium text-slate-700">
                Rooms Available
              </th>
              <th className="text-right py-3 px-4 font-medium text-slate-700">
                Rooms Sold
              </th>
              <th className="text-right py-3 px-4 font-medium text-slate-700">
                Rooms Revenue ({deal.currency})
              </th>
            </tr>
          </thead>
          <tbody className={getAnimationClass("table")}>
            {model.months.map((month, i) => (
              <tr
                key={i}
                className={`border-b border-slate-100 ${getAnimationClass(`month-${i}`)}`}
              >
                <td className="py-3 px-4 font-medium text-slate-900">
                  {monthNames[i]}
                </td>
                <td className="py-3 px-4 text-right">
                  <input
                    type="number"
                    value={month.adr || ""}
                    onChange={(e) =>
                      handleCellChange(i, "adr", Number(e.target.value) || 0)
                    }
                    className="w-24 px-2 py-1 border border-slate-300 rounded text-right"
                    min="0"
                  />
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="relative">
                    <input
                      type="number"
                      value={month.occPct || ""}
                      onChange={(e) =>
                        handleCellChange(
                          i,
                          "occPct",
                          Number(e.target.value) || 0,
                        )
                      }
                      className="w-20 px-2 py-1 border border-slate-300 rounded text-right pr-6"
                      min="0"
                      max="100"
                    />
                    <span className="absolute right-2 top-1 text-slate-500 pointer-events-none text-sm">
                      %
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(month.revpar, deal.currency)}
                </td>
                <td className="py-3 px-4 text-right text-slate-600">
                  {Math.round(month.roomsAvailable)?.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-right text-slate-600">
                  {Math.round(month.roomsSold)?.toLocaleString()}
                </td>
                <td className="py-3 px-4 text-right text-slate-600">
                  {formatCurrency(month.roomsRevenue, deal.currency)}
                </td>
              </tr>
            ))}

            {/* Totals Row */}
            <tr
              className={`border-t-2 border-slate-300 bg-slate-100 ${getAnimationClass("totals")}`}
            >
              <td className="py-4 px-4 font-bold text-slate-900">TOTALS</td>
              <td className="py-4 px-4 text-right font-bold text-slate-900">
                {formatCurrency(model.totals?.avgADR, deal.currency)}
              </td>
              <td className="py-4 px-4 text-right font-bold text-slate-900">
                {model.totals?.avgOccPct?.toFixed(1)}%
              </td>
              <td className="py-4 px-4 text-right font-bold text-slate-900">
                {formatCurrency(model.totals?.avgRevPAR, deal.currency)}
              </td>
              <td className="py-4 px-4 text-right font-bold text-slate-900">
                {Math.round(model.totals?.roomsAvailable)?.toLocaleString()}
              </td>
              <td className="py-4 px-4 text-right font-bold text-slate-900">
                {Math.round(model.totals?.roomsSold)?.toLocaleString()}
              </td>
              <td className="py-4 px-4 text-right font-bold text-slate-900">
                {formatCurrency(model.totals?.roomsRevenue, deal.currency)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Live Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-card">
        <h4 className="font-semibold text-slate-900 mb-4">
          Monthly Performance Chart
        </h4>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="currency"
                orientation="left"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                yAxisId="percent"
                orientation="right"
                tick={{ fontSize: 12 }}
                domain={[0, 100]}
              />
              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0]?.payload;
                  return (
                    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
                      <p className="font-semibold text-slate-900">{label}</p>
                      <p className="text-sm">
                        ADR: {formatCurrency(data.adr, deal.currency)}
                      </p>
                      <p className="text-sm">Occupancy: {data.occupancy}%</p>
                      <p className="text-sm">
                        RevPAR: {formatCurrency(data.revpar, deal.currency)}
                      </p>
                      <p className="text-sm">
                        Rooms Sold: {data.roomsSold?.toLocaleString()}
                      </p>
                      <p className="text-sm">
                        Revenue:{" "}
                        {formatCurrency(data.roomsRevenue, deal.currency)}
                      </p>
                    </div>
                  );
                }}
              />
              <Legend />
              {/* Currency bars */}
              <Bar
                yAxisId="currency"
                dataKey="adr"
                name="ADR"
                fill="#14b8a6"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="currency"
                dataKey="revpar"
                name="RevPAR"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              {/* Occupancy line */}
              <Line
                yAxisId="percent"
                type="monotone"
                dataKey="occupancy"
                name="Occupancy %"
                stroke="#f59e0b"
                strokeWidth={3}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Set ADR Dialog */}
      <SetAdrDialog
        isOpen={showSetAdrDialog}
        onClose={() => setShowSetAdrDialog(false)}
        onApply={handleSetAdr}
        currentAdr={model.months.length > 0 ? model.months[0].adr : 140}
      />

      {/* Action Buttons */}
      {!hideActions && (
        <div className="flex items-center space-x-3 pt-4 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saveState === "saving"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveState === "saving"}
            className={`${
              saveState === "success"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_40%)]"
            } text-white flex items-center space-x-2`}
          >
            {saveState === "saving" && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {saveState === "success" && <Check className="h-4 w-4" />}
            <span>
              {saveState === "saving" && "Saving..."}
              {saveState === "success" && "Saved"}
              {saveState === "idle" && "Save changes"}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}
